import { getCacheInfo, evictOldCacheEntries } from './svgCache';

const MONITORING_INTERVALS = [];

/**
 * Monitor SVG cache performance and memory usage
 * Compatible with Node 14
 * Only called when MAPS_ENVIRONMENT !== 'production' (gated in index.js)
 */
export function startCacheMonitoring() {
  // Clear any existing intervals
  stopCacheMonitoring();

  // Update cache stats periodically (for memory monitor component)
  // No console logging - data is available via getCacheMetrics() for UI components
  const statsInterval = setInterval(function () {
    // Just refresh the cache info periodically
    // The Memory Monitor component calls getCacheMetrics() directly
    getCacheInfo();
  }, 30000);

  MONITORING_INTERVALS.push(statsInterval);

  // Evict old entries periodically (every 10 minutes)
  // This helps prevent memory buildup in long-running containers
  const evictInterval = setInterval(function () {
    const cacheInfo = getCacheInfo();
    if (cacheInfo.svgCacheSize > 0) {
      // Evict old entries to prevent unbounded growth
      evictOldCacheEntries();
    }
  }, 600000); // 10 minutes

  MONITORING_INTERVALS.push(evictInterval);
}

/**
 * Stop cache monitoring
 */
export function stopCacheMonitoring() {
  MONITORING_INTERVALS.forEach(function (interval) {
    clearInterval(interval);
  });
  MONITORING_INTERVALS.length = 0;
}

/**
 * Get browser memory information if available
 */
export function getMemoryInfo() {
  const memoryInfo = {
    client: null,
    server: null
  };

  // Client-side memory (browser)
  if (typeof performance !== 'undefined' && performance.memory) {
    const mem = performance.memory;
    memoryInfo.client = {
      used: Math.round(mem.usedJSHeapSize / 1024 / 1024 * 100) / 100,
      total: Math.round(mem.totalJSHeapSize / 1024 / 1024 * 100) / 100,
      limit: Math.round(mem.jsHeapSizeLimit / 1024 / 1024 * 100) / 100,
      usage: Math.round((mem.usedJSHeapSize / mem.jsHeapSizeLimit) * 1000) / 10
    };
  }
  
  // Server-side memory (Node.js) - if running in Node environment
  if (typeof process !== 'undefined' && process.memoryUsage) {
    try {
      const nodeMem = process.memoryUsage();
      memoryInfo.server = {
        rss: Math.round(nodeMem.rss / 1024 / 1024 * 100) / 100,
        heapTotal: Math.round(nodeMem.heapTotal / 1024 / 1024 * 100) / 100,
        heapUsed: Math.round(nodeMem.heapUsed / 1024 / 1024 * 100) / 100,
        external: Math.round(nodeMem.external / 1024 / 1024 * 100) / 100,
        arrayBuffers: nodeMem.arrayBuffers ? Math.round(nodeMem.arrayBuffers / 1024 / 1024 * 100) / 100 : 0,
        heapUsage: nodeMem.heapTotal > 0 ? Math.round((nodeMem.heapUsed / nodeMem.heapTotal) * 1000) / 10 : 0
      };
    } catch (e) {
      console.error('Error getting memory info', e)
    }
  }

  return memoryInfo;
}

/**
 * Get current cache performance metrics
 * Only returns data when monitoring is enabled (gated by runtime config in index.js)
 */
export function getCacheMetrics() {
  const cacheInfo = getCacheInfo();
  const memoryInfo = getMemoryInfo();

  return {
    cache: cacheInfo,
    memory: memoryInfo
  };
}
